import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { morphSection } from '@theme/section-renderer';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';

/**
 * "Choose your gift" picker in the cart drawer.
 *
 * The theme owns the gift line: add / swap / remove here, all via the cart
 * AJAX API with section rendering so the drawer morphs like any other cart
 * change. The 100% discount itself is applied by a Shopify Function that
 * targets lines carrying the `_gwp` line property.
 *
 * Liquid flags a gift line that is no longer earned (below threshold, wrong
 * market, B2B, feature off) with `data-stale-key`; we remove it as soon as
 * that attribute appears, including after a morph.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [track] - Scrollable tile track.
 * @property {HTMLElement} [dots] - Pagination dots container.
 * @property {HTMLElement} [error] - Error message element.
 *
 * @extends {Component<Refs>}
 */
class CartGiftPicker extends Component {
  static observedAttributes = ['data-stale-key'];

  #busy = false;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  connectedCallback() {
    super.connectedCallback();
    this.#syncStale();
    this.#bindTrack();
    this.#renderDots();

    if (this.refs.track && 'ResizeObserver' in window) {
      this.#resizeObserver = new ResizeObserver(() => this.#renderDots());
      this.#resizeObserver.observe(this.refs.track);
    }

    // A drawer morph swaps our children (including the dots container) while
    // keeping this element, so rebuild the dots after every cart update.
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.#onCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.#onCartUpdate);
  }

  #onCartUpdate = () => {
    // Runs after the section morph that other listeners perform synchronously.
    setTimeout(() => {
      if (!this.isConnected) return;
      this.#bindTrack();
      this.#renderDots();
    }, 50);
  };

  /**
   * The theme's `on:` attribute binding does not cover scroll events, so the
   * track gets a native passive listener. Re-run after a morph replaces it.
   */
  #bindTrack() {
    const { track } = this.refs;
    if (!track || track.dataset.giftBound === '1') return;
    track.dataset.giftBound = '1';
    track.addEventListener('scroll', this.onTrackScroll, { passive: true });
  }

  /**
   * @param {string} name
   */
  attributeChangedCallback(name) {
    if (name === 'data-stale-key' && this.isConnected) this.#syncStale();
  }

  /**
   * Adds the chosen gift variant (quantity 1) with the hidden `_gwp` property.
   * @param {number | string} variantId
   */
  addGift = (variantId) => {
    if (!variantId) return;
    this.#run(async () => {
      const sections = await this.#add(variantId);
      this.#publish(sections);
    });
  };

  /**
   * Replaces the current gift with another variant.
   * @param {number | string} variantId
   */
  swapGift = (variantId) => {
    const currentKey = this.dataset.giftKey;
    if (!variantId) return;
    this.#run(async () => {
      if (currentKey) await this.#change(currentKey, 0, false);
      const sections = await this.#add(variantId);
      this.#publish(sections);
    });
  };

  /**
   * Removes the current gift line.
   */
  removeGift = () => {
    const key = this.dataset.giftKey;
    if (!key) return;
    this.#run(async () => {
      const sections = await this.#change(key, 0, true);
      this.#publish(sections);
    });
  };

  /**
   * Scrolls the picker into view (used by the "Change" link on the gift line).
   */
  focusPicker = () => {
    this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    const firstEnabled = this.querySelector('.cart-gift__btn:not(:disabled)');
    if (firstEnabled instanceof HTMLElement) firstEnabled.focus({ preventScroll: true });
  };

  /**
   * Updates the pagination dots as the track scrolls.
   */
  onTrackScroll = () => {
    this.#renderDots();
  };

  /** Desktop arrows: scroll one viewport of tiles at a time. */
  scrollPrev = () => this.#scrollBy(-1);
  scrollNext = () => this.#scrollBy(1);

  /**
   * @param {number} direction - -1 back, 1 forward
   */
  #scrollBy(direction) {
    const { track } = this.refs;
    if (!track) return;
    track.scrollBy({ left: direction * Math.max(track.clientWidth - 40, 80), behavior: 'smooth' });
  }

  /**
   * Removes a gift line Liquid has flagged as no longer earned.
   */
  #syncStale() {
    const key = this.dataset.staleKey;
    if (!key || this.#busy) return;
    this.#run(async () => {
      const sections = await this.#change(key, 0, true);
      this.#publish(sections);
    });
  }

  /**
   * Serialises cart operations and toggles the busy state.
   * @param {() => Promise<void>} task
   */
  async #run(task) {
    if (this.#busy) return;
    this.#busy = true;
    this.classList.add('cart-gift--busy');
    this.#showError('');

    try {
      await task();
    } catch (error) {
      this.#showError(error instanceof Error ? error.message : String(error));
    } finally {
      this.#busy = false;
      this.classList.remove('cart-gift--busy');
    }
  }

  /**
   * POST /cart/add with the gift marker property.
   * @param {number | string} variantId
   * @returns {Promise<Record<string, string> | undefined>} rendered sections
   */
  async #add(variantId) {
    const body = JSON.stringify({
      items: [
        {
          id: Number(variantId),
          quantity: 1,
          properties: { _gwp: this.dataset.marker || 'choose-your-gift' },
        },
      ],
      sections: this.#sectionIds.join(','),
      sections_url: window.location.pathname,
    });

    const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body }));
    const data = await response.json();

    if (!response.ok || data.status) {
      throw new Error(data.description || data.message || 'Could not add the gift');
    }

    return data.sections;
  }

  /**
   * POST /cart/change for a line key.
   * @param {string} key - line item key
   * @param {number} quantity
   * @param {boolean} withSections - request section markup
   * @returns {Promise<Record<string, string> | undefined>}
   */
  async #change(key, quantity, withSections) {
    /** @type {Record<string, unknown>} */
    const payload = { id: key, quantity };
    if (withSections) {
      payload.sections = this.#sectionIds.join(',');
      payload.sections_url = window.location.pathname;
    }

    const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body: JSON.stringify(payload) }));
    const data = await response.json();

    if (!response.ok || data.status) {
      throw new Error(data.description || data.message || 'Could not update the gift');
    }

    return data.sections;
  }

  /**
   * Morphs the drawer and tells the rest of the theme the cart changed.
   * @param {Record<string, string> | undefined} sections
   */
  #publish(sections) {
    const sectionId = this.dataset.sectionId;
    const html = sectionId && sections ? sections[sectionId] : undefined;

    let itemCount;
    if (html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const count = doc.querySelector('[ref="cartItemCount"]')?.textContent;
      itemCount = count ? parseInt(count, 10) : 0;
    }

    // cart-items-component morphs from event.detail.data.sections when the
    // event comes from another element; cart-icon reads itemCount.
    this.dispatchEvent(
      new CartUpdateEvent({}, this.id, {
        itemCount,
        source: 'cart-gift-picker',
        sections,
      })
    );

    if (sectionId && html && !document.querySelector('cart-items-component')) {
      morphSection(sectionId, html);
    }
  }

  get #sectionIds() {
    const ids = new Set();
    if (this.dataset.sectionId) ids.add(this.dataset.sectionId);
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
    });
    return Array.from(ids);
  }

  /**
   * @param {string} message
   */
  #showError(message) {
    const { error } = this.refs;
    if (!error) return;
    error.textContent = message;
    error.classList.toggle('hidden', !message);
  }

  /**
   * Builds pagination dots from the track's scroll width; hidden when all
   * tiles fit.
   */
  #renderDots() {
    const { track, dots, prev, next } = this.refs;
    if (!track || !dots) return;

    const maxScroll = track.scrollWidth - track.clientWidth;
    const scrollable = maxScroll > 4;

    // Arrows (desktop only via CSS): flag overflow, disable at either end
    this.toggleAttribute('data-scrollable', scrollable);
    if (prev instanceof HTMLButtonElement) prev.disabled = !scrollable || track.scrollLeft <= 2;
    if (next instanceof HTMLButtonElement) next.disabled = !scrollable || track.scrollLeft >= maxScroll - 2;

    const pages = Math.ceil(track.scrollWidth / Math.max(track.clientWidth, 1));
    if (pages <= 1) {
      dots.replaceChildren();
      return;
    }
    const active = maxScroll > 0 ? Math.round((track.scrollLeft / maxScroll) * (pages - 1)) : 0;

    if (dots.childElementCount !== pages) {
      dots.replaceChildren(
        ...Array.from({ length: pages }, () => {
          const dot = document.createElement('span');
          dot.className = 'cart-gift__dot';
          return dot;
        })
      );
    }

    Array.from(dots.children).forEach((dot, index) => {
      dot.classList.toggle('cart-gift__dot--active', index === active);
    });
  }
}

if (!customElements.get('cart-gift-picker')) {
  customElements.define('cart-gift-picker', CartGiftPicker);
}
